import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * What a screen knows about where the user is, for Help to resolve against.
 *
 * Both fields are optional and either may carry the answer on its own. linkVarName is
 * the strong one - the resolver scores it at 400 against 300 for an exact route and 150
 * for a route pattern.
 */
export interface HelpContextHint {

  /**
   * The context key, e.g. 'tower.backup' or 'scenario.characteristics'.
   *
   * Must match the linkVarName stored on a help item. It is a STRING MATCH, not a
   * foreign key: rename a tower in the application without updating its help item and
   * this stops matching silently. dbo.vw_pb_HelpCoverage is what catches that.
   */
  linkVarName?: string | null;

  /** The route the user was on, for the resolver to fall back to. */
  linkUrlPath?: string | null;
}

/**
 * Holds what the current screen knows, so Help can open where the user already was.
 *
 * WHY THIS EXISTS AT ALL, rather than Help reading the URL itself: this application has
 * no tower route. A tower is selected inside the scenario page and never appears in the
 * URL, so nothing in the address bar can say which one is open. Without a screen
 * publishing it, Help resolution can never get below the scenario page - which makes
 * this service the whole mechanism rather than a convenience.
 *
 * Deliberately tiny and passive. It stores a hint and hands it back; it does not call
 * the API, does not decide what to open, and has no opinion about Help. The screens
 * publish, the Help route consumes.
 */
@Injectable({
  providedIn: 'root'
})
export class HelpContextService {

  // ----------------- Constants ---------------------------------------------

  /**
   * The one route prefix that does NOT clear the hint.
   *
   * Everything else clears on navigation, which is what stops a stale tower leaking
   * into Help opened from an unrelated screen. Help itself has to be the exception, or
   * the hint would be gone by the time the Help route came to read it - navigating to
   * Help is precisely the moment it is needed.
   */
  protected readonly ctHelpRoutePrefix = '/help';

  // ----------------- State -------------------------------------------------

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly hint = signal<HelpContextHint | null>(null);

  /** What the last context-bearing screen published, or null. */
  public readonly current = this.hint.asReadonly();

  // ----------------- Constructor -------------------------------------------

  constructor() {

    // Clear when a navigation STARTS, except one into Help.
    //
    // A hint means "where the user was when they asked for help". Left to accumulate it
    // becomes a lie: open a scenario, wander to a screen that publishes nothing, ask
    // for help, and you would land on the tower you left ten minutes ago - confidently
    // and wrongly. Clearing means an unpublished screen falls back to the Help home
    // instead, which is honest.
    //
    // NavigationStart, NOT NavigationEnd, and this was a bug before it was a comment.
    // Angular creates the incoming component and runs its ngOnInit BEFORE it emits
    // NavigationEnd, so clearing on End wiped the hint the arriving screen had just
    // published - every publisher silently published nothing. Start fires before the
    // new component exists, so it clears the OUTGOING screen's hint and leaves the
    // incoming one's alone. Caught by checking what the service actually held after
    // loading /home; it read fine on the page.
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {

        if (!this.isHelpRoute(event.url)) this.hint.set(null);
      });
  }

  // ----------------- Publishing --------------------------------------------

  /**
   * Publishes what this screen knows. Call it whenever the answer changes, not only on
   * init - a tower selection changing mid-page is exactly the case this serves.
   *
   * Passing a hint with nothing useful in it clears instead, so a caller does not have
   * to special-case its own empty state.
   */
  public set(hint: HelpContextHint | null): void {

    const varName = this.trimmed(hint?.linkVarName);
    const urlPath = this.trimmed(hint?.linkUrlPath);

    if (!varName && !urlPath) {
      this.hint.set(null);
      return;
    }

    this.hint.set({ linkVarName: varName, linkUrlPath: urlPath });
  }

  /**
   * Publishes a context key, letting the service supply the current route.
   *
   * The common case: a screen knows what it is but has no reason to care what its own
   * URL is.
   */
  public setKey(linkVarName: string | null): void {

    this.set({ linkVarName, linkUrlPath: this.router.url });
  }

  /** Forgets the current hint. */
  public clear(): void {

    this.hint.set(null);
  }

  // ----------------- Helpers -----------------------------------------------

  /**
   * Turns a tower's type name into its context key - 'NW Device Mgmt' becomes
   * 'tower.nw-device-mgmt'.
   *
   * PASS THE TOWER TYPE, NOT THE USER'S LABEL FOR IT. On the scenario page those are
   * different things: currDealItem.tower is the type, currDealItem.name is what the
   * author called their instance of it. Help items exist per type, so passing the label
   * would match nothing for anyone who renamed theirs.
   *
   * Verified against dbo.pb_Tower: lowercasing and hyphenating spaces reproduces the
   * stored linkVarName for all fifteen IT Services towers exactly. The other towers in
   * that table - Telecom, FAO, Procurement, CCaaS and the labor-rate tiers - have no
   * help items yet, and resolve to their parent section instead of failing.
   */
  public towerKey(towerTypeName: string | null | undefined): string | null {

    const name = this.trimmed(towerTypeName);

    if (!name) return null;

    return 'tower.' + name.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Turns a practice area's name into its listing route - 'IT Labor Rates' becomes
   * '/scenarios/it-labor-rates'.
   *
   * This is what lets a scenario fall back to ITS OWN domain. Every help domain section
   * carries that route as its linkUrlPath, and the resolver exact-matches a route at
   * 300 - below a tower key at 400, above the /scenarios wildcard at 150. So publishing
   * it alongside the tower key gives the layering for free: the tower article when one
   * exists, otherwise the domain the reader is actually working in.
   *
   * Verified against dbo.pb_DealGroup: lowercasing and hyphenating spaces produces a
   * route that matches a real help section for all six active practice areas - CCaaS,
   * FAO, IT Labor Rates, IT Services, Procurement, Telecom. Same transform as
   * towerKey, and the same string-match fragility: rename a practice area without
   * renaming its help section and this stops matching silently.
   */
  public dealGroupRoute(dealGroupName: string | null | undefined): string | null {

    const name = this.trimmed(dealGroupName);

    if (!name) return null;

    return '/scenarios/' + name.toLowerCase().replace(/\s+/g, '-');
  }

  protected isHelpRoute(url: string): boolean {

    const path = (url ?? '').split('?')[0].split('#')[0];

    return path === this.ctHelpRoutePrefix || path.startsWith(this.ctHelpRoutePrefix + '/');
  }

  protected trimmed(value: string | null | undefined): string | null {

    const text = (value ?? '').trim();

    return text.length > 0 ? text : null;
  }
}
